import { Component, ChangeDetectionStrategy, input, output, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NgxEchartsModule } from 'ngx-echarts';
import { EChartsOption } from 'echarts';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { SVGIconModule } from '@progress/kendo-angular-icons';
import { SVGIcon, moreVerticalIcon } from '@progress/kendo-svg-icons';
import { DetectionTrendData } from '../../../../services/identity-security.service';

@Component({
  selector: 'app-detection-trend',
  standalone: true,
  imports: [CommonModule, NgxEchartsModule, ButtonsModule, SVGIconModule],
  templateUrl: './detection-trend.component.html',
  styleUrl: './detection-trend.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DetectionTrendComponent {
  trendData = input<DetectionTrendData | null>(null);
  isLoading = input<boolean>(true);
  /** Customer chose View Data from the 3-dots menu; parent opens the entity dialog. */
  openDetail = output<void>();

  public moreVerticalIcon: SVGIcon = moreVerticalIcon;
  public menuItems = [{ text: 'View Data' }];

  public onMenuSelect(event: { text?: string }): void {
    if (event?.text === 'View Data') {
      this.openDetail.emit();
    }
  }

  private getCssVar(name: string): string {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  }

  /**
   * Nothing to plot: no data at all, or a request that did not come back.
   *
   * Without this the chart renders an empty box with no axes and no explanation,
   * which reads as "no detections" — a claim nobody measured.
   */
  public hasNoData = computed(() => !this.trendData()?.labels?.length);

  public chartOptions = computed<EChartsOption>(() => {
    const trend = this.trendData();
    if (!trend?.labels?.length) return {};

    const primary = this.getCssVar('--color-primary');
    const gray500 = this.getCssVar('--gray-500');
    const gray300 = this.getCssVar('--gray-300');
    const gray100 = this.getCssVar('--gray-100');

    return {
      backgroundColor: 'transparent',
      grid: {
        left: 56,
        right: 20,
        top: 20,
        bottom: 30,
      },
      xAxis: {
        type: 'category',
        data: trend.labels,
        axisLabel: {
          fontSize: 10,
          color: gray500,
          interval: 6,
        },
        axisLine: { lineStyle: { color: gray300 } },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value',
        minInterval: 1,
        name: 'Detections',
        nameLocation: 'middle',
        nameGap: 32,
        nameRotate: 90,
        nameTextStyle: { fontSize: 11, color: gray500 },
        axisLabel: { fontSize: 10, color: gray500 },
        splitLine: { lineStyle: { color: gray100 } },
      },
      series: [
        {
          type: 'line',
          data: trend.values,
          smooth: true,
          symbol: 'none',
          lineStyle: {
            width: 2.5,
            color: primary,
          },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: primary + '40' }, // ~25% opacity
                { offset: 1, color: primary + '05' }, // ~2% opacity
              ],
            } as any,
          },
        },
      ],
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const p = params[0];
          return `${p.name}<br/><strong>${p.value}</strong> detection${p.value !== 1 ? 's' : ''}`;
        },
      },
    };
  });
}
